import { Field, Form } from "react-final-form";
import { SystemConfigLayout } from "../../components/SystemConfigLayout";
import { TextInput } from "../../../components/inputs/final_form/TextInput";
import { AdministrateSelectInput } from "../../components/inputs/AdministrateSelectInput";
import { composeValidators, numericInRange, required } from "../../../util/validators";
import { LoadingButton } from "../../../components/LoadingButton";
import { Badge, Card, CardBody, CardFooter, CardTitle, Col, FormGroup, FormText, Row, Spinner } from "reactstrap";
import { CircleCheck, CircleX, Info } from "lucide-react";
import { adminApi as api, useAdminAPI } from "../../../util/api";
import { useState } from "react";
import { camelizeKeys, snakifyKeys } from "../../../util/helpers";
import { DetailedErrorAlert } from "../../../components/DetailedErrorAlert";

export const CaseEscalationDashboard = () => {
  const [updateError, setUpdateError] = useState();

  const { data, error, isLoading } = useAdminAPI(`/admin/case_auto_escalations/get_instance`);

  const updateInstance = async (values) => {
    try {
      const params = snakifyKeys(values);

      await api.patch(`/admin/case_auto_escalations/update_instance`, { case_auto_escalation: params });
    } catch (e) {
      setUpdateError(e?.response?.data?.errors?.join());
    }
  };

  return (
    <>
      <SystemConfigLayout title="Case Auto Escalation Dashboard">
        <Form onSubmit={updateInstance} initialValues={camelizeKeys(data)}>
          {({ values, handleSubmit, submitting, submitSucceeded, invalid }) => {
            return (
              <>
                <Row>
                  <Col xs={3}>
                    <Card color="info" className="bg-opacity-25">
                      <div className="p-3 d-flex flex-column align-items-center gap-1">
                        <Info />

                        <p className="mb-0">Note:</p>

                        <small className="mb-0 text-left">
                          Logic will run everyday at 4am
                          <br />
                          to automatically escalate or round-robin Cases.
                        </small>
                      </div>
                    </Card>
                  </Col>

                  <Col>
                    {updateError && <DetailedErrorAlert className="mb-2" error={updateError} />}

                    {isLoading && (
                      <div className="d-flex flex-row justify-content-center align-items-center w-100 h-100">
                        <Spinner color="secondary">Loading...</Spinner>
                      </div>
                    )}
                    {data && (
                      <Card>
                        <CardBody>
                          <Row>
                            <Col>
                              <Card className="border-0 bg-info bg-opacity-25 mb-3">
                                <CardBody className="pb-0">
                                  <div className="d-flex flex-row-reverse w-100">
                                    <Badge color="info">Cases in Received Status for more than X business days.</Badge>
                                  </div>

                                  <FormGroup>
                                    <Field
                                      component={TextInput}
                                      name="newCaseNumberOfDays"
                                      type="number"
                                      min="0"
                                      label="Number of Days"
                                      validate={composeValidators(required, numericInRange(0, 200))}
                                      placeholder="between 0 and 200..."
                                    />
                                  </FormGroup>

                                  <Field
                                    component={AdministrateSelectInput}
                                    placeholder="Select one or more..."
                                    resourceUrl="/admin/accounts.json"
                                    labelKey="full_name_or_email"
                                    name="newCaseAccountIds"
                                    label="Excluded Accounts"
                                    valueKey="id"
                                    isMulti
                                    isClearable
                                    initialOptions={values.newCaseAccountOptions}
                                  />
                                </CardBody>
                              </Card>
                            </Col>
                          </Row>

                          <Row>
                            <Col>
                              <Card className="border-0 bg-warning bg-opacity-25">
                                <CardBody className="pb-0">
                                  <div className="d-flex flex-row-reverse w-100">
                                    <Badge color="warning">Cases with Task Due for more than X business days.</Badge>
                                  </div>

                                  <FormGroup>
                                    <Field
                                      component={TextInput}
                                      name="dueCaseNumberOfDays"
                                      type="number"
                                      min="0"
                                      label="Number of Days"
                                      validate={composeValidators(required, numericInRange(0, 200))}
                                      placeholder="between 0 and 200..."
                                    />
                                  </FormGroup>

                                  <Field
                                    component={AdministrateSelectInput}
                                    placeholder="Select one or more..."
                                    resourceUrl="/admin/accounts.json"
                                    labelKey="full_name_or_email"
                                    name="dueCaseAccountIds"
                                    label="Excluded Accounts"
                                    valueKey="id"
                                    isMulti
                                    isClearable
                                    initialOptions={values.dueCaseAccountOptions}
                                  />
                                </CardBody>
                              </Card>
                            </Col>
                          </Row>
                        </CardBody>

                        <CardFooter>
                          <div className="d-flex flex-row justify-content-between">
                            <LoadingButton color="primary" onClick={handleSubmit} loading={submitting} disabled={invalid}>
                              Save
                            </LoadingButton>

                            {submitSucceeded && !updateError && (
                              <small className="ms-auto mt-1">
                                <CircleCheck color="green" className="me-1" /> Updated!
                              </small>
                            )}

                            {submitSucceeded && updateError && (
                              <small className="ms-auto mt-1">
                                <CircleX color="red" className="me-1" /> Failed to Update!
                              </small>
                            )}
                          </div>
                        </CardFooter>
                      </Card>
                    )}
                  </Col>
                </Row>
              </>
            );
          }}
        </Form>
      </SystemConfigLayout>
    </>
  );
};
